import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Building2, Palette, Globe, LogOut, Ticket, Inbox, IndianRupee, Users, Wallet, Briefcase } from "lucide-react";
import client from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant, assetUrl } from "@/context/TenantContext";
import { logoColors } from "@/lib/logoColors";
import { AgencyAgentsPanel, AgencyWalletPanel, AgencyReportsPanel } from "@/components/agency/AgencyB2B";
import { LogoUpload, MarkupPanel, PaymentsPanel, EarningsPanel, PackagesPanel } from "@/components/agency/AgencyModules";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const day = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");

const AgencyLogin = () => {
  const { login } = useAuth();
  const [f, setF] = useState({ email: "", password: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const r = await login(f.email, f.password, "tenant_admin");
    setBusy(false);
    if (!r.ok) toast.error(r.message);
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#F5F7FB] px-4">
      <form onSubmit={submit} className="w-full max-w-sm rounded-2xl bg-white p-8 shadow-xl" data-testid="agency-login-form">
        <Building2 className="h-9 w-9 text-[#1B1F5B]" />
        <h1 className="mt-4 text-2xl font-bold text-[#1B1F5B]">Agency Admin</h1>
        <p className="mt-1 text-sm text-slate-500">Manage your white-label travel site.</p>
        <Input className="mt-6" type="email" placeholder="Email" value={f.email} data-testid="agency-login-email"
          onChange={(e) => setF({ ...f, email: e.target.value })} required />
        <Input className="mt-3" type="password" placeholder="Password" value={f.password} data-testid="agency-login-password"
          onChange={(e) => setF({ ...f, password: e.target.value })} required />
        <Button type="submit" disabled={busy} className="mt-5 w-full bg-[#1B1F5B] hover:bg-[#141844]" data-testid="agency-login-submit">
          {busy ? "Signing in…" : "Sign in"}
        </Button>
        <p className="mt-4 text-center text-xs text-slate-500">New agency? <Link to="/agency-signup" className="font-semibold text-[#1B1F5B]" data-testid="agency-login-signup-link">Apply here</Link></p>
      </form>
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, id }) => (
  <div className="rounded-xl border border-slate-100 bg-white p-5" data-testid={`agency-stat-${id}`}>
    <Icon className="h-5 w-5 text-[#F5B32B]" />
    <p className="mt-3 text-2xl font-bold text-[#1B1F5B]">{value}</p>
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
  </div>
);

const Field = ({ label, children }) => (
  <label className="block text-sm font-medium text-slate-700">
    {label}
    <div className="mt-1.5">{children}</div>
  </label>
);

const ColorInput = ({ value, onChange, id }) => (
  <div className="flex items-center gap-2">
    <input type="color" value={value || "#000000"} onChange={(e) => onChange(e.target.value)}
      className="h-10 w-12 cursor-pointer rounded border" data-testid={`${id}-picker`} />
    <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${id}-input`} />
  </div>
);

const BrandingForm = ({ initial, onSaved }) => {
  const [t, setT] = useState(initial);
  const [saving, setSaving] = useState(false);
  const ci = t.contact_info || {};
  const set = (k, v) => setT({ ...t, [k]: v });
  const setCi = (k, v) => setT({ ...t, contact_info: { ...ci, [k]: v } });
  const matchLogo = async (url = t.logo_url) => {
    if (!url) return toast.error("Add a logo first");
    try {
      const c = await logoColors(assetUrl(url));
      setT((x) => ({ ...x, logo_url: url, primary_color: c.primary, secondary_color: c.secondary }));
      toast.success("Colours picked from your logo — click Save branding to apply");
    } catch { toast.error("Could not read colours from this logo"); }
  };
  const save = async () => {
    setSaving(true);
    try {
      const { name, custom_domain, logo_url, primary_color, secondary_color, contact_info } = t;
      const r = await client.put("/tenant-admin/branding", { name, custom_domain: custom_domain || null, logo_url, primary_color, secondary_color, contact_info });
      toast.success("Branding saved");
      onSaved(r.data.tenant);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_320px]">
      <div className="space-y-5 rounded-2xl bg-white p-6">
        <Field label="Agency name"><Input value={t.name || ""} onChange={(e) => set("name", e.target.value)} data-testid="branding-name-input" /></Field>
        <Field label="Logo">
          <div className="flex gap-2">
            <Input value={t.logo_url || ""} placeholder="https://…/logo.png or upload" onChange={(e) => set("logo_url", e.target.value)} data-testid="branding-logo-input" />
            <LogoUpload onUploaded={(url) => { onSaved({ ...t, logo_url: url }); matchLogo(url); }} />
          </div>
        </Field>
        <Button type="button" variant="outline" onClick={() => matchLogo()} data-testid="branding-match-logo-btn"
          className="border-dashed"><Palette className="mr-1.5 h-4 w-4" />Match colours to my logo</Button>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Primary colour"><ColorInput id="branding-primary" value={t.primary_color} onChange={(v) => set("primary_color", v)} /></Field>
          <Field label="Secondary colour"><ColorInput id="branding-secondary" value={t.secondary_color} onChange={(v) => set("secondary_color", v)} /></Field>
        </div>
        <Field label="Custom domain">
          <Input value={t.custom_domain || ""} placeholder="www.youragency.com" onChange={(e) => set("custom_domain", e.target.value)} data-testid="branding-domain-input" />
        </Field>
        <div className="grid gap-5 sm:grid-cols-2">
          <Field label="Support email"><Input value={ci.email || ""} onChange={(e) => setCi("email", e.target.value)} data-testid="branding-email-input" /></Field>
          <Field label="Support phone"><Input value={ci.phone || ""} onChange={(e) => setCi("phone", e.target.value)} data-testid="branding-phone-input" /></Field>
        </div>
        <Field label="Address"><Input value={ci.address || ""} onChange={(e) => setCi("address", e.target.value)} data-testid="branding-address-input" /></Field>
        <Button onClick={save} disabled={saving} className="bg-[#1B1F5B] hover:bg-[#141844]" data-testid="branding-save-btn">
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>
      <BrandPreview t={t} />
    </div>
  );
};

const BrandPreview = ({ t }) => {
  const root = useTenant().rootDomain;
  return (
  <div className="space-y-4" data-testid="branding-preview">
    <div className="overflow-hidden rounded-2xl border bg-white">
      <div className="flex items-center justify-between border-b px-4 py-3">
        {t.logo_url ? <img src={assetUrl(t.logo_url)} alt="" className="h-8" /> : <b style={{ color: t.primary_color }}>{t.name}</b>}
        <span className="rounded-full px-3 py-1 text-xs font-semibold text-white" style={{ background: t.primary_color }}>Login</span>
      </div>
      <div className="p-5" style={{ background: t.primary_color }}>
        <p className="text-sm font-semibold text-white">Book flights & holidays</p>
        <span className="mt-3 inline-block rounded-md px-4 py-2 text-xs font-bold" style={{ background: t.secondary_color, color: t.primary_color }}>Search</span>
      </div>
    </div>
    <div className="rounded-xl bg-[#FFF9EC] p-4 text-xs leading-relaxed text-slate-600">
      <Globe className="mb-2 h-4 w-4 text-[#B57A00]" />
      To go live on your own domain, add a <b>CNAME</b> record pointing <b>{t.custom_domain || "www.youragency.com"}</b> to the platform host, then ask the platform team to link the domain.
      {t.subdomain && <> Your subdomain: <b>{t.subdomain}.{root}</b></>}
    </div>
  </div>
);
};

const Table = ({ rows, cols, empty, testId }) => (
  <div className="overflow-x-auto rounded-2xl bg-white" data-testid={testId}>
    <table className="w-full text-left text-sm">
      <thead className="bg-[#F5F7FB] text-xs uppercase text-slate-500">
        <tr>{cols.map((c) => <th key={c[0]} className="px-4 py-3">{c[0]}</th>)}</tr>
      </thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} className="px-4 py-10 text-center text-slate-400">{empty}</td></tr>}
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-slate-100">{cols.map((c) => <td key={c[0]} className="px-4 py-3">{c[1](r)}</td>)}</tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function TenantAdmin() {
  const { user, loading, logout } = useAuth();
  const { refresh, applyTheme } = useTenant();
  const [tenant, setTenant] = useState(null);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enq, setEnq] = useState([]);
  const isTA = user?.role === "tenant_admin";
  // Agency colours apply inside the portal; restore the site's own theme when leaving.
  useEffect(() => () => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadStats = () => client.get("/tenant-admin/stats").then((r) => setStats(r.data));
  useEffect(() => {
    if (!isTA) return;
    client.get("/tenant-admin/branding").then((r) => { setTenant(r.data.tenant); applyTheme(r.data.tenant); }); // eslint-disable-line
    loadStats();
    client.get("/tenant-admin/bookings").then((r) => setBookings(r.data.bookings));
    client.get("/tenant-admin/enquiries").then((r) => setEnq([...r.data.enquiries, ...r.data.holiday_enquiries]));
  }, [isTA]);

  if (loading) return null;
  if (!isTA) return <AgencyLogin />;

  return (
    <div className="min-h-screen bg-[#F5F7FB]" data-testid="agency-admin-dashboard">
      <header className="bg-[#1B1F5B] text-white">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-white/60">Agency Admin</p>
            <h1 className="text-xl font-bold" data-testid="agency-admin-name">{tenant?.name || "…"}</h1>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="text-sm text-white/80 hover:text-white" data-testid="agency-view-site-link">View site</Link>
            <Button variant="secondary" size="sm" onClick={logout} data-testid="agency-logout-btn"><LogOut className="mr-1 h-4 w-4" />Logout</Button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-7xl space-y-8 px-4 py-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Stat id="wallet" icon={Wallet} label="Agency wallet" value={fmt(stats?.wallet)} />
          <Stat id="agents" icon={Briefcase} label="B2B agents" value={stats?.agents ?? "—"} />
          <Stat id="bookings" icon={Ticket} label="Bookings" value={stats?.bookings ?? "—"} />
          <Stat id="revenue" icon={IndianRupee} label="Revenue" value={fmt(stats?.revenue)} />
          <Stat id="enquiries" icon={Inbox} label="Enquiries" value={stats?.enquiries ?? "—"} />
          <Stat id="customers" icon={Users} label="Customers" value={stats?.customers ?? "—"} />
        </div>
        <Tabs defaultValue="branding">
          <TabsList className="h-auto flex-wrap">
            <TabsTrigger value="branding" data-testid="agency-tab-branding"><Palette className="mr-1.5 h-4 w-4" />Branding & Domain</TabsTrigger>
            <TabsTrigger value="agents" data-testid="agency-tab-agents">B2B Agents</TabsTrigger>
            <TabsTrigger value="wallet" data-testid="agency-tab-wallet">Wallet</TabsTrigger>
            <TabsTrigger value="reports" data-testid="agency-tab-reports">Reports</TabsTrigger>
            <TabsTrigger value="markup" data-testid="agency-tab-markup">Markup</TabsTrigger>
            <TabsTrigger value="packages" data-testid="agency-tab-packages">Packages</TabsTrigger>
            <TabsTrigger value="payments" data-testid="agency-tab-payments">Payments</TabsTrigger>
            <TabsTrigger value="earnings" data-testid="agency-tab-earnings">Earnings</TabsTrigger>
            <TabsTrigger value="bookings" data-testid="agency-tab-bookings">Bookings</TabsTrigger>
            <TabsTrigger value="enquiries" data-testid="agency-tab-enquiries">Enquiries</TabsTrigger>
          </TabsList>
          <TabsContent value="branding" className="mt-6">
            {tenant && <BrandingForm initial={tenant} onSaved={(t) => { setTenant(t); applyTheme(t); }} />}
          </TabsContent>
          <TabsContent value="agents" className="mt-6"><AgencyAgentsPanel onChange={loadStats} /></TabsContent>
          <TabsContent value="wallet" className="mt-6"><AgencyWalletPanel /></TabsContent>
          <TabsContent value="reports" className="mt-6"><AgencyReportsPanel /></TabsContent>
          <TabsContent value="markup" className="mt-6"><MarkupPanel /></TabsContent>
          <TabsContent value="packages" className="mt-6"><PackagesPanel /></TabsContent>
          <TabsContent value="payments" className="mt-6"><PaymentsPanel /></TabsContent>
          <TabsContent value="earnings" className="mt-6"><EarningsPanel /></TabsContent>
          <TabsContent value="bookings" className="mt-6">
            <Table testId="agency-bookings-table" rows={bookings} empty="No bookings on your site yet."
              cols={[["Ref", (b) => b.pnr || b.booking_id], ["Type", (b) => b.type || "flight"], ["Route", (b) => b.route],
                ["Customer", (b) => b.passenger?.name || b.user_email], ["Date", (b) => b.date || day(b.created_at)],
                ["Total", (b) => fmt(b.total)], ["Status", (b) => b.status]]} />
          </TabsContent>
          <TabsContent value="enquiries" className="mt-6">
            <Table testId="agency-enquiries-table" rows={enq} empty="No enquiries yet."
              cols={[["Name", (e) => e.name], ["Phone", (e) => e.phone], ["Type", (e) => e.type || "holiday"],
                ["Status", (e) => e.status], ["Received", (e) => day(e.created_at)]]} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
